import { useState, useRef, useEffect } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import {
  ArrowLeft,
  Loader2,
  AlertCircle,
  Plus,
  Trash2,
  BookOpen,
  FolderOpen,
  PlayCircle,
  ChevronDown,
  ChevronRight,
} from 'lucide-react'
import { adminApi, curriculumBuilderApi, type AdminContent } from '@/api/admin'
import { CreateContentModal } from './CreateContentModal'
import { ContentMetadataForm } from './ContentMetadataForm'
import { ModuleLessonEditor } from './ModuleLessonEditor'

const LEVEL_ICONS: Record<string, React.ElementType> = {
  curriculum: BookOpen,
  course: FolderOpen,
  module: FolderOpen,
  lesson: PlayCircle,
}

const LEVEL_COLORS: Record<string, string> = {
  curriculum: 'text-blue-600 bg-blue-50',
  course: 'text-purple-600 bg-purple-50',
  module: 'text-amber-600 bg-amber-50',
  lesson: 'text-green-600 bg-green-50',
}

const LEVEL_LABELS: Record<string, string> = {
  curriculum: '커리큘럼',
  course: '코스',
  module: '모듈',
  lesson: '레슨',
}

/** Allowed child types for each parent type */
const ALLOWED_CHILDREN: Record<string, string[]> = {
  curriculum: ['course', 'module', 'lesson'],
  course: ['module', 'lesson'],
  module: ['lesson'],
  lesson: [],
}

interface CurriculumEditorProps {
  curriculumId: number
}

export function CurriculumEditor({ curriculumId }: CurriculumEditorProps) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [selectedId, setSelectedId] = useState<number | null>(curriculumId)
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set([curriculumId]))
  const [createModal, setCreateModal] = useState<{ level: 'curriculum' | 'course' | 'module'; parentId: number; parentTitle: string } | null>(null)

  // Fetch curriculum with hierarchy
  const { data: hierarchy, isLoading, error } = useQuery({
    queryKey: ['curriculum-builder', 'hierarchy', curriculumId],
    queryFn: () => adminApi.getContentHierarchy(curriculumId, true),
  })

  const selectedContent = selectedId ? findInHierarchy(hierarchy, selectedId) : null

  const toggleExpand = (id: number) => {
    setExpandedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const handleNodeSelect = (id: number) => {
    setSelectedId(id)
  }

  const handleContentUpdated = () => {
    queryClient.invalidateQueries({ queryKey: ['curriculum-builder', 'hierarchy', curriculumId] })
  }

  const handleCreated = (content: AdminContent) => {
    setCreateModal(null)
    queryClient.invalidateQueries({ queryKey: ['curriculum-builder', 'hierarchy', curriculumId] })
    setExpandedIds((prev) => new Set([...prev, content.parentId!]))
    setSelectedId(content.id)
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    )
  }

  if (error || !hierarchy) {
    return (
      <div className="p-6">
        <div className="flex items-center gap-2 p-4 text-red-600 bg-red-50 rounded-lg">
          <AlertCircle className="w-5 h-5" />
          <span>커리큘럼을 불러올 수 없습니다.</span>
        </div>
      </div>
    )
  }

  return (
    <div className="h-[calc(100vh-4rem)] flex flex-col">
      {/* Header */}
      <div className="px-6 py-4 bg-white border-b border-slate-200 flex items-center gap-4 shrink-0">
        <button
          onClick={() => navigate('/contents/curriculum-builder')}
          className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <h1 className="text-lg font-semibold text-slate-900">{hierarchy.title}</h1>
          <p className="text-sm text-slate-500">{LEVEL_LABELS[hierarchy.type]} 편집</p>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 flex overflow-hidden">
        {selectedContent && hasDirectLessons(selectedContent) ? (
          /* Node with direct lessons: full-width ModuleLessonEditor (tree hidden) */
          <ModuleLessonEditor
            key={selectedId}
            parentId={selectedContent.id}
            parentTitle={selectedContent.title}
            parentType={selectedContent.type}
            lessons={(selectedContent.children || []).filter((c: any) => c.type === 'lesson').map((c: any) => ({
              id: c.id,
              title: c.title,
              qid: c.qid,
            }))}
            curriculumId={curriculumId}
            onUpdated={handleContentUpdated}
            onBack={() => {
              const parent = findParentOf(hierarchy, selectedContent.id)
              setSelectedId(parent ? parent.id : curriculumId)
            }}
          />
        ) : (
          /* Normal 2-pane: Tree + Detail */
          <>
            {/* Left: Tree */}
            <div className="w-80 border-r border-slate-200 bg-white overflow-y-auto shrink-0">
              <div className="p-3">
                <TreeNodeView
                  node={hierarchy}
                  level={0}
                  selectedId={selectedId}
                  expandedIds={expandedIds}
                  onSelect={handleNodeSelect}
                  onToggle={toggleExpand}
                  onAddChild={(parentId, parentTitle, childLevel) => {
                    if (childLevel === 'lesson') {
                      // Switch to lesson editor mode for this parent
                      setSelectedId(parentId)
                      setExpandedIds((prev) => new Set([...prev, parentId]))
                    } else {
                      setCreateModal({ level: childLevel as 'curriculum' | 'course' | 'module', parentId, parentTitle })
                    }
                  }}
                  onRemoveChild={(parentId, childId) => {
                    if (confirm('이 항목을 제거하시겠습니까?')) {
                      curriculumBuilderApi.removeChildFromParent(parentId, childId).then(() => {
                        queryClient.invalidateQueries({ queryKey: ['curriculum-builder', 'hierarchy', curriculumId] })
                        if (selectedId === childId) setSelectedId(parentId)
                      })
                    }
                  }}
                />
              </div>
            </div>

            {/* Right: Detail form */}
            <div className="flex-1 overflow-y-auto bg-slate-50 p-6">
              {selectedContent ? (
                <ContentMetadataForm
                  key={selectedId}
                  content={selectedContent}
                  onUpdated={handleContentUpdated}
                />
              ) : (
                <div className="flex items-center justify-center h-full text-slate-400">
                  좌측 트리에서 항목을 선택하세요
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Modals */}
      {createModal && (
        <CreateContentModal
          level={createModal.level}
          parentId={createModal.parentId}
          parentTitle={createModal.parentTitle}
          onClose={() => setCreateModal(null)}
          onCreated={handleCreated}
        />
      )}
    </div>
  )
}

// ============================================================================
// Tree Node Component
// ============================================================================

interface TreeNodeViewProps {
  node: any
  level: number
  selectedId: number | null
  expandedIds: Set<number>
  onSelect: (id: number) => void
  onToggle: (id: number) => void
  onAddChild: (parentId: number, parentTitle: string, childLevel: string) => void
  onRemoveChild: (parentId: number, childId: number) => void
}

function TreeNodeView({ node, level, selectedId, expandedIds, onSelect, onToggle, onAddChild, onRemoveChild }: TreeNodeViewProps) {
  const hasChildren = node.children && node.children.length > 0
  const isExpanded = expandedIds.has(node.id)
  const isSelected = selectedId === node.id
  const Icon = LEVEL_ICONS[node.type] || FolderOpen
  const colorClass = LEVEL_COLORS[node.type] || 'text-slate-600 bg-slate-50'

  const allowedChildren = ALLOWED_CHILDREN[node.type] || []
  const canAddChild = allowedChildren.length > 0

  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  // Close dropdown on outside click
  useEffect(() => {
    if (!menuOpen) return
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [menuOpen])

  return (
    <div>
      <div
        className={`group flex items-center gap-1 px-2 py-1.5 rounded-lg cursor-pointer transition-colors ${
          isSelected ? 'bg-blue-50 text-blue-700' : 'hover:bg-slate-50 text-slate-700'
        }`}
        style={{ paddingLeft: `${level * 16 + 8}px` }}
      >
        {/* Expand toggle */}
        <button
          onClick={(e) => { e.stopPropagation(); onToggle(node.id) }}
          className={`w-5 h-5 flex items-center justify-center shrink-0 ${hasChildren ? '' : 'invisible'}`}
        >
          {isExpanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
        </button>

        {/* Icon + Label */}
        <div className="flex items-center gap-2 flex-1 min-w-0" onClick={() => onSelect(node.id)}>
          <div className={`w-6 h-6 rounded flex items-center justify-center shrink-0 ${colorClass}`}>
            <Icon className="w-3.5 h-3.5" />
          </div>
          <span className="text-sm truncate">{node.title}</span>
        </div>

        {/* Add child: dropdown menu if multiple options, direct action if single */}
        {canAddChild && (
          <div className="relative" ref={menuRef}>
            {allowedChildren.length === 1 ? (
              <button
                onClick={(e) => { e.stopPropagation(); onAddChild(node.id, node.title, allowedChildren[0]) }}
                className="w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-all"
                title={`${LEVEL_LABELS[allowedChildren[0]]} 추가`}
              >
                <Plus className="w-3.5 h-3.5" />
              </button>
            ) : (
              <button
                onClick={(e) => { e.stopPropagation(); setMenuOpen(!menuOpen) }}
                className="w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-all"
                title="하위 항목 추가"
              >
                <Plus className="w-3.5 h-3.5" />
              </button>
            )}
            {menuOpen && (
              <div className="absolute right-0 top-7 z-50 bg-white border border-slate-200 rounded-lg shadow-lg py-1 min-w-[140px]">
                {allowedChildren.map((childType) => {
                  const ChildIcon = LEVEL_ICONS[childType] || FolderOpen
                  const childColor = LEVEL_COLORS[childType] || 'text-slate-600 bg-slate-50'
                  return (
                    <button
                      key={childType}
                      onClick={(e) => {
                        e.stopPropagation()
                        setMenuOpen(false)
                        onAddChild(node.id, node.title, childType)
                      }}
                      className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                    >
                      <div className={`w-5 h-5 rounded flex items-center justify-center shrink-0 ${childColor}`}>
                        <ChildIcon className="w-3 h-3" />
                      </div>
                      {LEVEL_LABELS[childType]} 추가
                    </button>
                  )
                })}
              </div>
            )}
          </div>
        )}
        {level > 0 && (
          <button
            onClick={(e) => {
              e.stopPropagation()
              onRemoveChild(0, node.id)
            }}
            className="w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-all"
            title="제거"
          >
            <Trash2 className="w-3 h-3" />
          </button>
        )}
      </div>

      {/* Children */}
      {isExpanded && hasChildren && (
        <div>
          {node.children.map((child: any) => (
            <TreeNodeView
              key={child.id}
              node={child}
              level={level + 1}
              selectedId={selectedId}
              expandedIds={expandedIds}
              onSelect={onSelect}
              onToggle={onToggle}
              onAddChild={onAddChild}
              onRemoveChild={(_, childId) => onRemoveChild(node.id, childId)}
            />
          ))}
        </div>
      )}
    </div>
  )
}

// ============================================================================
// Helpers
// ============================================================================

/**
 * A node should show the lesson editor when:
 * - It's a module (always manages lessons)
 * - It's a curriculum or course that has at least one direct lesson child
 */
function hasDirectLessons(node: any): boolean {
  if (!node) return false
  if (node.type === 'module') return true
  if (node.type === 'curriculum' || node.type === 'course') {
    return (node.children || []).some((c: any) => c.type === 'lesson')
  }
  return false
}

function findInHierarchy(node: any, id: number): any | null {
  if (!node) return null
  if (node.id === id) return node
  if (node.children) {
    for (const child of node.children) {
      const found = findInHierarchy(child, id)
      if (found) return found
    }
  }
  return null
}

function findParentOf(node: any, targetId: number): any | null {
  if (!node || !node.children) return null
  for (const child of node.children) {
    if (child.id === targetId) return node
    const found = findParentOf(child, targetId)
    if (found) return found
  }
  return null
}

